<template>
  <div>
    <v-card flat>
      <v-card-text>
        <v-row dense>
          <v-col
            v-for="doc in items"
            :key="doc.id"
            cols="12"
            sm="6"
            md="4"
            class="d-flex align-items-stretch"
          >
            <v-card variant="flat" class="my-4 flex-grow-1 app-content-card">
              <v-card-text class="text-left">
                <h3>{{ $t("candidateTabs.documents.field") }}: {{ doc.Document }}</h3>
                <v-divider></v-divider>
                <p>
                  <strong>{{ $t("candidateTabs.documents.description") }}:</strong>
                  {{ doc.Document_descriptions }}
                </p>
                <p>
                  <strong>{{ $t("candidateTabs.documents.date") }}:</strong> {{ formatDate(doc.Date) }}
                </p>
                <v-btn color="primary" @click="downloadDocument(doc)" class="mt-2">
                  <v-icon left>mdi-download</v-icon>
                  {{ $t("candidateTabs.documents.download") }}
                </v-btn>
              </v-card-text>
              <v-card-actions>
                <v-btn icon @click="openEditDocumentDialog(doc)">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon color="error" @click="confirmDeleteDocument(doc.id)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        <v-btn
          color="primary"
          @click="triggerDocUpload"
          class="mb-4 d-flex align-items-stretch"
        >
          {{ $t("candidateTabs.documents.addButton") }}
        </v-btn>

        <!-- input ascuns pentru upload -->
        <input
          ref="docUploadInput"
          type="file"
          accept=".pdf,.txt,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
          style="display:none"
          @change="handleDocFileChange"
        />
      </v-card-text>
    </v-card>

    <v-dialog v-model="addDocumentDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateTabs.documents.addTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="addDocument">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.documents.field')} *`" v-model="newDocument.Document" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="$t('candidateTabs.documents.descriptionLabel')" v-model="newDocument.Document_descriptions" outlined></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.documents.date')} *`" type="date" v-model="newDocument.Date" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.add") }}</v-btn>
                  <v-btn variant="text" @click="closeAddDocumentDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDocumentDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateTabs.documents.editTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="updateDocument">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.documents.field')} *`" v-model="editDocument.Document" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="$t('candidateTabs.documents.descriptionLabel')" v-model="editDocument.Document_descriptions" outlined></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.documents.date')} *`" type="date" v-model="editDocument.Date" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.save") }}</v-btn>
                  <v-btn variant="text" @click="closeEditDocumentDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";

// Other documents CRUD tab (incl. file upload + download) extracted from
// CandidateDetails.vue. The parent reloads documents on `refresh`.
export default defineComponent({
  name: "CandidateDocumentsTab",
  props: {
    candidateId: { type: [String, Number], required: true },
    items: { type: Array, default: () => [] },
  },
  emits: ["refresh", "notify"],
  data() {
    return {
      addDocumentDialog: false,
      newDocument: { Document: "", Document_descriptions: "", Date: "" },
      editDocumentDialog: false,
      editDocument: { id: null, Document: "", Document_descriptions: "", Date: "" },
    };
  },
  computed: {
    rules() {
      return { required: (value) => !!value || this.$t("candidateTabs.common.requiredField") };
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      return dateString.split("T")[0];
    },
    triggerDocUpload() {
      if (this.$refs && this.$refs.docUploadInput) {
        this.$refs.docUploadInput.click();
      }
    },
    async handleDocFileChange(e) {
      try {
        const file = e && e.target && e.target.files && e.target.files[0];
        if (!file) return;

        // 1) Upload în /uploads
        const fd = new FormData();
        fd.append("file", file); // cheie: "file"
        const uploadRes = await axios.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // încearcă să scoți path-ul din răspuns (acoperim mai multe variante)
        const filePath =
          (uploadRes &&
            uploadRes.data &&
            (uploadRes.data.filePath || uploadRes.data.path || uploadRes.data.url || uploadRes.data.file)) ||
          "";

        // 2) Creează înregistrarea în other_documents
        const today = new Date().toISOString().split("T")[0];
        const payload = {
          id_foreign: this.candidateId,
          Document: file.name,
          Document_descriptions: "",
          Date: today,
          FilePath: filePath,
        };
        await axios.post("/candidati/otherdocuments", payload);

        // 3) Reîmprospătează lista (părintele re-fetch)
        this.$emit("refresh");

        this.$emit("notify", { message: this.$t("candidateTabs.documents.uploaded"), color: "success" });
      } catch (err) {
        console.error("Eroare la încărcarea documentului:", err);
        this.$emit("notify", { message: this.$t("candidateTabs.documents.uploadError"), color: "error" });
      } finally {
        if (this.$refs && this.$refs.docUploadInput) {
          this.$refs.docUploadInput.value = "";
        }
      }
    },
    async downloadDocument(doc) {
      try {
        const response = await axios.get(`/download-document/${doc.id}`, {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", doc.Document);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Eroare la descărcarea documentului:", error);
        this.$emit("notify", { message: this.$t("candidateTabs.documents.downloadError"), color: "error" });
      }
    },
    openAddDocumentDialog() {
      this.newDocument = { Document: "", Document_descriptions: "", Date: "" };
      this.addDocumentDialog = true;
    },
    closeAddDocumentDialog() {
      this.addDocumentDialog = false;
    },
    addDocument() {
      if (!this.newDocument.Document.trim() || !this.newDocument.Date.trim()) {
        this.$emit("notify", { message: this.$t("candidateTabs.common.requiredEmpty"), color: "error" });
        return;
      }
      const payload = {
        id_foreign: this.candidateId,
        Document: this.newDocument.Document,
        Document_descriptions: this.newDocument.Document_descriptions || null,
        Date: this.newDocument.Date,
      };
      axios
        .post("/candidati/otherdocuments", payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.documents.added"), color: "success" });
          this.addDocumentDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la adăugarea documentului:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.documents.addError"), color: "error" });
        });
    },
    openEditDocumentDialog(doc) {
      this.editDocument = { ...doc };
      this.editDocumentDialog = true;
    },
    closeEditDocumentDialog() {
      this.editDocumentDialog = false;
    },
    updateDocument() {
      if (!this.editDocument.Document.trim() || !this.editDocument.Date.trim()) {
        this.$emit("notify", { message: this.$t("candidateTabs.common.requiredEmpty"), color: "error" });
        return;
      }
      const documentId = this.editDocument.id;
      const payload = {
        id_foreign: this.candidateId,
        Document: this.editDocument.Document,
        Document_descriptions: this.editDocument.Document_descriptions || null,
        Date: this.editDocument.Date,
      };
      axios
        .put(`/candidati/otherdocuments/${documentId}`, payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.documents.updated"), color: "success" });
          this.editDocumentDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la actualizarea documentului:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.documents.updateError"), color: "error" });
        });
    },
    confirmDeleteDocument(id) {
      if (confirm(this.$t("candidateTabs.documents.deleteConfirm"))) {
        this.deleteDocument(id);
      }
    },
    deleteDocument(id) {
      axios
        .delete(`/candidati/otherdocuments/${id}`)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.documents.deleted"), color: "success" });
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la ștergerea documentului:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.documents.deleteError"), color: "error" });
        });
    },
  },
});
</script>
